import React, { useState, useEffect } from 'react';
import Tooltip from '@mui/material/Tooltip';
import {
  Container,
  Typography,
  Box,
  Card,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  IconButton,
  Button,
  Chip,
  Divider,
  CircularProgress,
  Alert,
  Badge,
  Paper,
} from '@mui/material';
import {
  Notifications as NotificationsIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  Info as InfoIcon,
  Warning as WarningIcon,
  Delete as DeleteIcon,
  Check as CheckIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material';
import { supabase } from '../lib/supabase';
import { TABLES } from '../lib/tables';
import { useAuth } from '../context/AuthContext';

export default function Notifications() {
  const { profile } = useAuth();
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [unreadCount, setUnreadCount] = useState(0);
  const [error, setError] = useState('');

  useEffect(() => {
    let isMounted = true;
    
    if (profile) {
      loadNotifications(isMounted);
      
      
      const channel = supabase
        .channel('notifications_channel')
        .on(
          'postgres_changes',
          {
            event: 'INSERT',
            schema: 'public',
            table: TABLES.notifications,
            filter: `id_user=eq.${profile.id}`,
          },
          (payload) => {
            if (isMounted) {
              setNotifications(prev => [payload.new, ...prev]);
              setUnreadCount(prev => prev + 1);
            }
          }
        )
        .subscribe((status, err) => {
          // inchide canalul realtime ca sa evite bucla de warning-uri; polling-ul ramane activ
          if (status === 'CHANNEL_ERROR' || status === 'TIMED_OUT') {
            console.warn('Canal Realtime notificări indisponibil:', status, err?.message || '');
            supabase.removeChannel(channel);
          }
        });

      return () => {
        isMounted = false;
        supabase.removeChannel(channel);
      };
    }
    
    return () => { isMounted = false; };
  }, [profile]);

  const loadNotifications = async (isMounted = true) => {
    if (!profile) return;
    
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from(TABLES.notifications)
        .select('*')
        .eq('id_user', profile.id)
        .order('created_at', { ascending: false })
        .limit(50);

      if (error) throw error;
      if (!isMounted) return;

      setNotifications(data || []);
      
      
      const unread = data?.filter(n => !n.citita).length || 0;
      setUnreadCount(unread);

    } catch (err) {
      if (isMounted) {
        setError('Eroare la încărcarea notificărilor');
      }
      console.error(err);
    } finally {
      if (isMounted) {
        setLoading(false);
      }
    }
  };

  const markAsRead = async (notificationId) => {
    try {
      const { error } = await supabase
        .from(TABLES.notifications)
        .update({ citita: true })
        .eq('id', notificationId);

      if (error) throw error;

      
      setNotifications(prev =>
        prev.map(n =>
          n.id === notificationId ? { ...n, citita: true } : n
        )
      );
      
      setUnreadCount(prev => Math.max(0, prev - 1));

    } catch (err) {
      console.error('Error marking notification as read:', err);
    }
  };

  const markAllAsRead = async () => {
    try {
      const unreadIds = notifications
        .filter(n => !n.citita)
        .map(n => n.id);

      if (unreadIds.length === 0) return;

      const { error } = await supabase
        .from(TABLES.notifications)
        .update({ citita: true })
        .in('id', unreadIds);

      if (error) throw error;

      
      setNotifications(prev =>
        prev.map(n => ({ ...n, citita: true }))
      );
      
      setUnreadCount(0);

    } catch (err) {
      console.error('Error marking all as read:', err);
    }
  };

  const deleteNotification = async (notificationId) => {
    try {
      const { error } = await supabase
        .from(TABLES.notifications)
        .delete()
        .eq('id', notificationId);

      if (error) throw error;

      
      setNotifications(prev =>
        prev.filter(n => n.id !== notificationId)
      );
      
      
      const deletedNotification = notifications.find(n => n.id === notificationId);
      if (deletedNotification && !deletedNotification.citita) {
        setUnreadCount(prev => Math.max(0, prev - 1));
      }

    } catch (err) {
      console.error('Error deleting notification:', err);
    }
  };

  const deleteAllRead = async () => {
    try {
      const readIds = notifications
        .filter(n => n.citita)
        .map(n => n.id);

      if (readIds.length === 0) return;

      const { error } = await supabase
        .from(TABLES.notifications)
        .delete()
        .in('id', readIds);

      if (error) throw error;

      
      setNotifications(prev =>
        prev.filter(n => !n.citita)
      );

    } catch (err) {
      console.error('Error deleting read notifications:', err);
    }
  };

  const getNotificationIcon = (type) => {
    switch (type) {
      case 'success': return <CheckCircleIcon color="success" />;
      case 'error': return <ErrorIcon color="error" />;
      case 'warning': return <WarningIcon color="warning" />;
      default: return <InfoIcon color="info" />;
    }
  };

  const getNotificationColor = (type) => {
    switch (type) {
      case 'success': return 'success.light';
      case 'error': return 'error.light';
      case 'warning': return 'warning.light';
      default: return 'info.light';
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / (1000 * 60));
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

    if (diffMins < 60) {
      return `Acum ${diffMins} ${diffMins === 1 ? 'minut' : 'minute'}`;
    } else if (diffHours < 24) {
      return `Acum ${diffHours} ${diffHours === 1 ? 'oră' : 'ore'}`;
    } else if (diffDays === 1) {
      return 'Ieri';
    } else if (diffDays < 7) {
      return `Acum ${diffDays} ${diffDays === 1 ? 'zi' : 'zile'}`;
    } else {
      return date.toLocaleDateString('ro-RO');
    }
  };

  if (loading) {
    return (
      <Container maxWidth="md" sx={{ py: 4, px: { xs: 0, sm: 3 } }}>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="50vh">
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="md" sx={{ py: 4, px: { xs: 0, sm: 3 } }}>
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
          <Badge badgeContent={unreadCount} color="error">
            <NotificationsIcon sx={{ fontSize: 40 }} color="primary" />
          </Badge>
          <Box>
            <Typography variant="h4" fontWeight="bold">
              Notificări
            </Typography>
            <Typography variant="body1" color="text.secondary">
              {notifications.length === 0 
                ? 'Nu ai notificări' 
                : `${notifications.length} notificări (${unreadCount} necitite)`
              }
            </Typography>
          </Box>
        </Box>

        {/* Toolbar */}
        {notifications.length > 0 && (
          <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
              <Button
                startIcon={<CheckIcon />}
                onClick={markAllAsRead}
                variant="outlined"
                size="small"
                disabled={unreadCount === 0}
              >
                Marchează toate ca citite
              </Button>
              
              <Button
                startIcon={<DeleteIcon />}
                onClick={deleteAllRead}
                variant="outlined"
                size="small"
                color="error"
                disabled={notifications.filter(n => n.citita).length === 0}
              >
                Șterge notificările citite
              </Button>
              
              <Button
                startIcon={<RefreshIcon />}
                onClick={loadNotifications}
                variant="outlined"
                size="small"
              >
                Reîncarcă
              </Button>
            </Box>
          </Paper>
        )}
      </Box>

      {/* Error message */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {/* Empty state */}
      {notifications.length === 0 && !loading && (
        <Card sx={{ textAlign: 'center', py: 8 }}>
          <NotificationsIcon sx={{ fontSize: 60, color: 'text.secondary', mb: 2 }} />
          <Typography variant="h6" color="text.secondary" gutterBottom>
            Nicio notificare momentan
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Vei primi notificări când cererile tale sunt procesate sau când apar actualizări
          </Typography>
        </Card>
      )}

      {/* Notifications list */}
      {notifications.length > 0 && (
        <Card variant="outlined">
          <List disablePadding>
            {notifications.map((notification, index) => (
              <React.Fragment key={notification.id}>
                {index > 0 && <Divider />}
                
                <ListItem
                  alignItems="flex-start"
                  sx={{
                    backgroundColor: notification.citita ? 'transparent' : 'action.hover',
                    '&:hover': {
                      backgroundColor: 'action.hover',
                    },
                    py: 2,
                  }}
                  secondaryAction={
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      {!notification.citita && (
                        <Tooltip title="Marchează ca citită">
                          <IconButton
                            size="small"
                            onClick={() => markAsRead(notification.id)}
                          >
                            <CheckIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                      <Tooltip title="Șterge">
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => deleteNotification(notification.id)}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  }
                >
                  <ListItemAvatar>
                    <Box
                      sx={{
                        width: 40,
                        height: 40,
                        borderRadius: '50%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        backgroundColor: getNotificationColor(notification.tip),
                      }}
                    >
                      {getNotificationIcon(notification.tip)}
                    </Box>
                  </ListItemAvatar>
                  
                  <ListItemText
                    primary={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                        <Typography variant="subtitle1" fontWeight="medium">
                          {notification.titlu}
                        </Typography>
                        {!notification.citita && (
                          <Chip
                            label="Nou"
                            color="primary"
                            size="small"
                            sx={{ height: 20, fontSize: '0.7rem' }}
                          />
                        )}
                        <Typography variant="caption" color="text.secondary" sx={{ ml: 'auto' }}>
                          {formatDate(notification.created_at)}
                        </Typography>
                      </Box>
                    }
                    secondary={
                      <Typography
                        variant="body2"
                        color="text.primary"
                        sx={{
                          whiteSpace: 'pre-wrap',
                          wordBreak: 'break-word',
                        }}
                      >
                        {notification.mesaj}
                      </Typography>
                    }
                  />
                </ListItem>
              </React.Fragment>
            ))}
          </List>
        </Card>
      )}

      {/* Footer */}
      <Box sx={{ mt: 4, textAlign: 'center' }}>
        <Typography variant="body2" color="text.secondary">
          Notificările se reînnoiesc automat. Cele mai vechi notificări se șterg automat după 30 de zile.
        </Typography>
      </Box>
    </Container>
  );
}
